/**
 * Responsibility: turn tags and sets into something the grouping grid can draw.
 *
 * Kept out of the pages because both the collection and the likes page need
 * the same two answers, and a second copy would be the place the two quietly
 * start disagreeing about what "held" counts.
 *
 * **Held counts distinct cards.** Three copies of one card is one card towards
 * a grouping, and counting holdings would let a pile of duplicates read as
 * most of a run.
 */
import type { CollectionItem, CollectionTag, LikedCard, SetSummary } from '../../api/client';
import type { Grouping } from '../shelf/GroupingGrid';

/** Enough images to recognise a grouping by, and no more. */
const PREVIEWS = 4;

/** Images worth showing: a card with no art contributes nothing. */
function previewsOf(images: (string | null)[]): (string | null)[] {
  return images.filter((url): url is string => Boolean(url)).slice(0, PREVIEWS);
}

function distinct(keys: string[]): number {
  return new Set(keys).size;
}

/**
 * One tile per sub-collection.
 *
 * Every tag gets a tile even with nothing in it, because starting a run
 * before owning any of it is the normal way to begin and a grouping that
 * vanished until its first card would look broken.
 */
export function tagGroupings(
  tags: CollectionTag[],
  items: CollectionItem[],
  liked: LikedCard[] = [],
): Grouping[] {
  return tags.map((tag) => {
    const mine = items.filter((item) => (item.tags ?? []).includes(tag.id));
    const wanted = liked.filter((card) => (card.tags ?? []).includes(tag.id));
    return {
      key: tag.id,
      name: tag.name,
      note: tag.note || null,
      held: distinct(mine.map((item) => item.product_key)),
      // Cards found but not owned. Counted apart from held, because one
      // number cannot say whether a run is nearly done or merely located.
      wanted: distinct(wanted.map((card) => card.product_key)),
      total: tag.goal,
      previews: previewsOf([
        ...mine.map((item) => item.image_url),
        ...wanted.map((card) => card.image_url),
      ]),
    };
  });
}

/**
 * One tile per set owned from.
 *
 * Driven by the server's summary rather than counted here, because the total
 * comes from the population universe and refuses whenever it cannot be
 * trusted. A total of null renders as a count with no fraction.
 */
export function setGroupings(sets: SetSummary[], items: CollectionItem[]): Grouping[] {
  // Holdings only. The server counts a set's completion from what is owned,
  // so drawing liked cards onto the tile would illustrate it with cards the
  // figure beneath does not include.
  const byLabel = new Map<string, (string | null)[]>();
  for (const item of items) {
    if (!item.set_label) continue;
    byLabel.set(item.set_label, [...(byLabel.get(item.set_label) ?? []), item.image_url]);
  }

  return sets.map((row) => ({
    key: row.label,
    name: row.label,
    note: row.year ? String(row.year) : null,
    held: row.held,
    total: row.total,
    previews: previewsOf(byLabel.get(row.label) ?? []),
  }));
}
